import React from 'react';
import { twMerge } from 'tailwind-merge';
import { Spinner, SpinnerColor } from '../Spinner/Spinner';
import { FontWeight, Heading } from '../../tokens';

export enum LiliButtonType {
  PRIMARY,
  SECONDARY,
  TERTIARY,
  SKIP,
  DESTRUCTIVE,
  BACK,
}
export interface LiliButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'onClick' | 'title' | 'id'
> {
  id: string;
  toolTipId?: string;
  testId?: string;
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  isLoading?: boolean;
  type?: LiliButtonType;
  'data-testid'?: string;
  /** never add override to the coloring */
}

export const LiliButton = ({
  title,
  onClick,
  disabled,
  type = LiliButtonType.PRIMARY,
  icon,
  suffixIcon,
  isLoading,
  toolTipId,
  id,
  className,
  testId,
  ...otherProps
}: LiliButtonProps) => {
  const innerOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isLoading && onClick) {
      onClick(e);
    }
  };
  const getClassNameByType = () => {
    switch (type) {
      case LiliButtonType.PRIMARY:
        return 'bg-surface-brand-action-03 hover:bg-surface-brand-action-04 text-text-main-primary-inverse';
      case LiliButtonType.SECONDARY:
        return 'border-[1px] border-outline-primary-bw-05 hover:bg-surface-primary-bw-02 text-text-brand-primary';
      case LiliButtonType.TERTIARY:
        return 'bg-surface-brand-action-00 hover:bg-surface-brand-action-01 text-text-brand-primary';
      case LiliButtonType.SKIP:
        return 'text-text-main-secondary hover:text-text-main-tertiary';
      case LiliButtonType.DESTRUCTIVE:
        return 'bg-surface-negative-primary hover:bg-surface-negative-primary-hover text-text-main-primary-inverse';
      case LiliButtonType.BACK:
        return 'text-text-main-primary hover:bg-surface-primary-bw-02';
      default:
        return '';
    }
  };
  const getDisabledClassNameByType = () => {
    switch (type) {
      case LiliButtonType.PRIMARY:
        return 'bg-surface-primary-bw-05 text-text-main-primary-inverse';
      case LiliButtonType.SECONDARY:
        return 'border-[1px] border-outline-primary-bw-05 text-text-main-disabled';
      case LiliButtonType.TERTIARY:
        return 'bg-surface-primary-bw-05 text-text-main-primary-inverse';
      case LiliButtonType.SKIP:
        return 'text-text-main-disabled';
      case LiliButtonType.DESTRUCTIVE:
        return 'bg-surface-negative-secondary text-text-main-primary-inverse';
      case LiliButtonType.BACK:
        return 'hover:bg-none text-text-main-disabled';
      default:
        return '';
    }
  };

  const getSpinnerColorByType = () => {
    switch (type) {
      case LiliButtonType.PRIMARY:
        return SpinnerColor.White;
      case LiliButtonType.SECONDARY:
        return SpinnerColor.Blue;
      case LiliButtonType.TERTIARY:
        return SpinnerColor.Blue;
      case LiliButtonType.SKIP:
        return SpinnerColor.Blue;
      case LiliButtonType.DESTRUCTIVE:
        return SpinnerColor.White;
      case LiliButtonType.BACK:
        return SpinnerColor.Blue;
      default:
        return undefined;
    }
  };
  const generalStyle = 'px-[12px] py-[14px] max-h-[53px] rounded-[8px] w-full';
  const style = twMerge(
    generalStyle,
    disabled ? getDisabledClassNameByType() : getClassNameByType(),
    className
  );

  return (
    <button
      id={id}
      onClick={innerOnClick}
      disabled={disabled}
      className={style}
      type='button'
      data-tooltip-id={toolTipId}
      data-testid={testId || otherProps['data-testid']}
      {...otherProps}>
      <div className='flex gap-[8px] align-middle items-center justify-center h-full w-full '>
        {isLoading ? (
          <Spinner spinnerColor={getSpinnerColorByType()} height='1.5rem' width='1.5rem' />
        ) : (
          <>
            {icon && <span>{icon}</span>}
            {title && (
              <Heading.H9
                className='text-inherit text-[length:inherit]'
                fontWeight={FontWeight.MEDIUM}>
                {title}
              </Heading.H9>
            )}
            {suffixIcon && <span>{suffixIcon}</span>}
          </>
        )}
      </div>
    </button>
  );
};
